import { useContext } from 'react'

import { useTranslation } from 'react-i18next'
import { useDataScope } from 'scrivito'

import { MobileFilterContext } from './MobileFilterContext'
import { MobileFilterSelect } from './MobileFilterSelect'
import { Option } from '@/Atomic/Molecules/Inputs/Select/types'
import { SimpleSelect } from '@/Atomic/Molecules/SimpleSelect'
import { useFilterValues } from '@/Components/FormContext'
import { SchemaAttributes } from '@/Data/types'
import { getSchemaName } from '@/utils/dataClassMap'
import { getSchema } from '@/utils/getSchema'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'

interface StatusFilterProps {
  dataClassName?: string
  filterKey?: string
  renderLabel?: (hasValue: boolean, onClear: () => void) => React.ReactNode
  group?: string
}

export const StatusFilter: React.FC<StatusFilterProps> = ({
  dataClassName: dataClassNameProp,
  filterKey = 'status',
  renderLabel,
  group,
}) => {
  const { t } = useTranslation()
  const dataScope = useDataScope()
  const { values, setValues } = useFilterValues()
  const { isMobile } = useContext(MobileFilterContext)

  const dataClassName = dataClassNameProp || dataScope?.dataClassName() || ''

  const { schema } = getSchema(getSchemaName(dataClassName))

  // Return null if schema is not loaded or doesn't have attributes
  if (!schema) return null

  const schemaAttributes = getLocalizedSchemaAttributes(
    schema?.get('attributes') as SchemaAttributes,
  )

  const statusFilters = schemaAttributes?.['status']?.enumValues

  if (!statusFilters) return null

  const options: Option[] = [
    { label: t('common.filter.allStatuses'), value: 'all' },
    ...statusFilters.map((filter) => ({
      label: filter.title,
      value: filter.value,
    })),
  ]

  const currentValue = values[filterKey] || 'all'
  const selectedOption = options.find((o) => o.value === currentValue)
  const hasValue = currentValue !== 'all'

  const handleChange = (newValue: Option) => {
    const val = newValue.value as string
    if (val === 'all') {
      setValues((prev) => {
        const { [filterKey]: _removed, ...rest } = prev
        return rest
      })
    } else {
      setValues((prev) => ({ ...prev, [filterKey]: val }))
    }
  }

  const handleClear = () => {
    setValues((prev) => {
      const { [filterKey]: _removed, ...rest } = prev
      return rest
    })
  }

  if (isMobile) {
    return (
      <>
        {renderLabel ? (
          renderLabel(hasValue, handleClear)
        ) : (
          <label className="mobile-filter-label">
            {t('common.filter.status')}
          </label>
        )}
        <MobileFilterSelect
          name="status-select"
          options={options}
          value={selectedOption}
          onChange={handleChange}
          title={t('common.filter.status')}
        />
      </>
    )
  }

  return (
    <>
      {renderLabel && renderLabel(hasValue, handleClear)}
      <SimpleSelect
        name="status-select"
        options={options}
        value={selectedOption}
        onChange={handleChange}
        group={group}
      />
    </>
  )
}
